import { FileSearch, Layers } from "lucide-react";
import type * as React from "react";

import type { ManagerDealListItem } from "@/api/client";
import { EmptyState } from "@/components/ui/empty-state";
import { SortableHeader } from "@/components/ui/sortable-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  tableRowLinkClassName,
} from "@/components/ui/table";
import { formatAud } from "@/lib/currency";
import { CHANNEL_PARTNER } from "@/lib/role-labels";
import { type SortValue } from "@/lib/tableSort";
import { useTableSort } from "@/lib/useTableSort";
import { cn } from "@/lib/utils";

import { ManagerDealStatusBadge } from "./ManagerDealStatusBadge";

/** Sortable columns for the manager ledger (CHP-417). Status keeps its CHP-364
 * group toggle, so it is not a sortable column here. */
type ManagerDealSortColumn =
  "deal_id" | "client" | "value" | "partner" | "owner" | "registered" | "expiry";

/** Module-level so the `useTableSort` memo dependency stays stable. The Owner
 * column sorts on the lead's name string (CHP-417 individuals rule), not the
 * "(PO)/(BDM)" suffix. */
function managerDealSortValue(d: ManagerDealListItem, column: ManagerDealSortColumn): SortValue {
  switch (column) {
    case "deal_id":
      return d.deal_id_display;
    case "client":
      return d.client_name;
    case "value":
      return d.total_contract_value;
    case "partner":
      return d.channel_partner_company;
    case "owner":
      return d.lead_bdm_name;
    case "registered":
      return d.submission_timestamp ? new Date(d.submission_timestamp) : null;
    case "expiry":
      return d.target_close_date ? new Date(d.target_close_date) : null;
  }
}

export interface ManagerDealsDataGridProps {
  deals: ManagerDealListItem[];
  /** Row click — navigates to the standalone /dashboard/manager/deals/$dealId. */
  onDealClick: (dealId: string) => void;
  emptyMessage?: string;
  /** Optional secondary line under the empty-state title. */
  emptyHint?: string;
  /** Optional CTA (e.g. a register-on-behalf button) shown in the empty state. */
  emptyAction?: React.ReactNode;
  /** CHP-364: rows are currently grouped by status (caller sorts them). */
  statusGrouped?: boolean;
  /** CHP-364: clicking the Status header toggles status grouping. */
  onToggleStatusGroup?: () => void;
  /** CHP-809: fired when a column sort engages, so the caller can clear status
   * grouping — otherwise the Layers highlight claims "grouped by status" while
   * the rows are actually ordered by the clicked column. */
  onColumnSort?: () => void;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString("en-AU") : "—";
}

/** "Name (PO)" / "Name (BDM)" — the lead's role tag from `lead_bdm_is_po`. */
function ownerText(d: ManagerDealListItem): string {
  if (!d.lead_bdm_name) return "—";
  if (d.lead_bdm_is_po == null) return d.lead_bdm_name;
  return `${d.lead_bdm_name} (${d.lead_bdm_is_po ? "PO" : "BDM"})`;
}

/** Master Pipeline Ledger for the CM/RM/RA view.
 *
 * Columns (CHP-330): Deal ID, Client, Value, Channel Partner Name,
 * Owner (PO/BDM), Status, Registration date, Expiry date. The Quick Action
 * column was removed (decisions happen in the review drawer). "Registration
 * date" is `submission_timestamp`; "Expiry date" is `target_close_date`;
 * either shows "—" when null. */
export function ManagerDealsDataGrid({
  deals,
  onDealClick,
  emptyMessage,
  emptyHint,
  emptyAction,
  statusGrouped,
  onToggleStatusGroup,
  onColumnSort,
}: ManagerDealsDataGridProps) {
  // CHP-417: client-side column sort. Unsorted, `sortedRows === deals`, so the
  // caller's default order (incl. CHP-364 status grouping) shows through.
  const { sort, toggle, sortedRows } = useTableSort(deals, managerDealSortValue);

  // CHP-809: a column sort overrides status grouping, so notify the caller to
  // clear the group toggle — keeping the Layers highlight in sync with the
  // actual row ordering. The sort itself is still applied by useTableSort.
  const handleColumnToggle = (column: ManagerDealSortColumn) => {
    onColumnSort?.();
    toggle(column);
  };

  if (deals.length === 0) {
    return (
      <EmptyState
        icon={FileSearch}
        title={emptyMessage ?? "No deals in this view."}
        hint={emptyHint}
        action={emptyAction}
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableHeader column="deal_id" sort={sort} onToggle={handleColumnToggle}>
            Deal ID
          </SortableHeader>
          <SortableHeader column="client" sort={sort} onToggle={handleColumnToggle}>
            Client
          </SortableHeader>
          <SortableHeader column="value" sort={sort} onToggle={handleColumnToggle} align="right">
            Value
          </SortableHeader>
          <SortableHeader column="partner" sort={sort} onToggle={handleColumnToggle}>
            {CHANNEL_PARTNER} Name
          </SortableHeader>
          <SortableHeader column="owner" sort={sort} onToggle={handleColumnToggle}>
            Owner (PO/BDM)
          </SortableHeader>
          <TableHead scope="col">
            {onToggleStatusGroup ? (
              <button
                type="button"
                onClick={onToggleStatusGroup}
                aria-pressed={statusGrouped}
                data-testid="manager-deal-status-group-toggle"
                className="flex items-center gap-1 hover:text-foreground"
              >
                Status
                <Layers
                  className={cn(
                    "h-3.5 w-3.5",
                    statusGrouped ? "text-primary" : "text-muted-foreground",
                  )}
                  aria-hidden
                />
              </button>
            ) : (
              "Status"
            )}
          </TableHead>
          <SortableHeader column="registered" sort={sort} onToggle={handleColumnToggle}>
            Registration date
          </SortableHeader>
          <SortableHeader column="expiry" sort={sort} onToggle={handleColumnToggle}>
            Expiry date
          </SortableHeader>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedRows.map((d) => (
          <TableRow
            interactive
            key={d.id}
            data-testid={`manager-deal-row-${d.id}`}
            // CHP-305: bind the open target per-row in JS (Safari does not
            // honour `position: relative` on a <tr>, breaking the CSS
            // stretched-link overlay so a single deal opened from any row).
            onClick={() => onDealClick(d.id)}
          >
            <TableCell>
              {/* Primary control — keyboard-focusable and named; the row
                  onClick above is the cross-browser pointer path. */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDealClick(d.id);
                }}
                className={cn(
                  "font-medium text-primary underline-offset-4 hover:underline",
                  tableRowLinkClassName,
                )}
                data-testid={`manager-deal-open-${d.id}`}
              >
                {d.deal_id_display ?? "View"}
              </button>
            </TableCell>
            <TableCell>{d.client_name}</TableCell>
            <TableCell className="text-right">{formatAud(d.total_contract_value)}</TableCell>
            <TableCell>{d.channel_partner_company ?? "—"}</TableCell>
            <TableCell>{ownerText(d)}</TableCell>
            <TableCell>
              <ManagerDealStatusBadge status={d.deal_status} />
              {/* Collision-lock indicator (deals-gap wave 2): the backend only
                  serialises these while a hold is UNEXPIRED, so a plain
                  null-check is enough — no clock math. */}
              {d.sla_hold_until && d.sla_holder_name ? (
                <p
                  className="mt-1 text-xs text-muted-foreground"
                  data-testid={`manager-deal-hold-${d.id}`}
                >
                  Under Review by {d.sla_holder_name}
                </p>
              ) : null}
            </TableCell>
            <TableCell data-testid={`manager-deal-submitted-${d.id}`}>
              {formatDate(d.submission_timestamp)}
            </TableCell>
            <TableCell data-testid={`manager-deal-closing-${d.id}`}>
              {formatDate(d.target_close_date)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
